/**
 * Página de configurações do tenant
 * 
 * Tabs:
 * - Conta: Informações da conta e limites do plano atual
 * 
 * RELEASE: 1.3.0 - 2026-09-26
 * - Nova aba Checklist (ativação do módulo Template de Checklist/Vistoria)
 * RELEASE: 1.2.0 - 2026-09-08
 * - Removidas as abas Credenciais e Perfis de Permissão (já existem no catálogo
 *   de integrações e na Central de Permissões, respectivamente)
 * RELEASE: 1.1.0 - 2024-12-27
 * - Adicionada aba de Credenciais
 */

import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User, Shield, FileSignature, Megaphone, Calculator, Car, ListChecks,
  Copy, Check, ExternalLink, Route,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { SignatureProvidersSettings } from '@/components/tenant/signatures';
import { AffiliateBrandingTab } from '@/components/tenant/affiliate-branding/AffiliateBrandingTab';
import { useReferralModuleAvailable } from '@/hooks/useReferralModuleAvailable';
import { SettingsInfoRow as InfoRow } from '@/components/tenant/SettingsInfoRow';
import { usePermissions } from '@/hooks/usePermissions';
import { FinancialSettingsCard } from '@/components/tenant/financial/FinancialSettingsCard';
// Aba do módulo de Veículos (flag + categorias do tipo "Outros")
import { VehiclesSettingsTab } from '@/components/tenant/vehicles/VehiclesSettingsTab';
// Aba do módulo de Tarefas (submódulos Retenção/Jurídico)
import { TasksSettingsTab } from '@/components/tenant/tasks/TasksSettingsTab';
// Aba do módulo Workflow (esteira comercial parametrizável — flag workflow_enabled)
import { WorkflowSettingsTab } from '@/components/tenant/workflow/WorkflowSettingsTab';
// Aba do módulo Checklist (Template de Checklist + Vistoria) — 2026-09-26
import { ChecklistSettingsTab } from '@/components/tenant/checklist/ChecklistSettingsTab';
import { StockSettingsTab } from '@/components/tenant/stock/StockSettingsTab';
import { Boxes, Wrench } from 'lucide-react';
import { ServiceOrderSettingsTab } from '@/components/tenant/service-orders/ServiceOrderSettingsTab';

const TenantSettings = () => {
  const { currentTenant } = useAuth();
  // Gate por role: somente perfis administrador (tenant_admin) e root_admin veem as abas restritas.
  // Supervisores e agentes nunca devem ver, mesmo com permissões granulares.
  const { isAdmin } = usePermissions();
  const { isAvailable: affiliateAvailable } = useReferralModuleAvailable();


  // Labels para exibição de planos e status
  const planLabels: Record<string, string> = {
    free: 'Gratuito',
    starter: 'Starter',
    professional: 'Profissional',
    enterprise: 'Enterprise',
  };

  const statusLabels: Record<string, string> = {
    trial: 'Trial',
    active: 'Ativo',
    suspended: 'Suspenso',
    cancelled: 'Cancelado',
  };

  // Cor do bullet de status (visual discreto, sem fundo agressivo)
  const statusDot: Record<string, string> = {
    trial: 'bg-amber-500',
    active: 'bg-emerald-500',
    suspended: 'bg-destructive',
    cancelled: 'bg-muted-foreground',
  };

  /**
   * Busca limites efetivos do tenant via RPC `get_tenant_effective_limits`,
   * que respeita add-ons contratados e módulos habilitados.
   * Substitui o select direto em `subscription_plans`.
   */
  const tenantId = currentTenant?.id;
  const { data: limits, isLoading: loadingLimits } = useQuery({
    queryKey: ['tenant-effective-limits', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_tenant_effective_limits', {
        p_tenant_id: tenantId!,
      });
      if (error) throw error;
      return data as Record<string, any>;
    },
  });

  // Helpers de formatação locais
  const shortId = (id?: string | null) =>
    id ? id.replace(/-/g, '').slice(0, 8).toUpperCase() : '--------';

  const formatNumber = (n?: number | null) =>
    typeof n === 'number' ? new Intl.NumberFormat('pt-BR').format(n) : '—';

  const formatStorage = (mb?: number | null) => {
    if (typeof mb !== 'number') return '—';
    if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
    return `${mb} MB`;
  };

  const formatDate = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  // Copiar para área de transferência com feedback
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = async (value: string, key: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header compacto - padrão Contatos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Configurações</h1>
          <p className="text-xs text-muted-foreground">
            Gerencie as configurações da sua conta e permissões
          </p>
        </div>
      </div>

      {/* Tabs Estilizadas */}
      <Tabs defaultValue="account" className="space-y-6">
        {/* Wrapper com rolagem horizontal: em telas menores a barra de abas
            passa a rolar em vez de quebrar/cortar os itens. */}
        <div className="-mx-1 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]">
          <TabsList className="inline-flex w-max flex-nowrap bg-muted/50 p-1">
          <TabsTrigger value="account" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <User className="h-4 w-4" />
            Conta
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="signatures" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <FileSignature className="h-4 w-4" />
              Assinatura Digital
            </TabsTrigger>
          )}
          {/*
            Aba Financeiro: sempre visível para admins, mesmo sem add-on.
            Tenants sem o módulo Financeiro contratado podem usar até
            10 cobranças gratuitas/mês — o banner dentro do card
            (FreeTierLimitBanner) comunica esse limite. Mesma regra
            já aplicada em /financial/charges, /subscriptions e
            /installments.
          */}
          {isAdmin && (
            <TabsTrigger value="financial" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Calculator className="h-4 w-4" />
              Financeiro
            </TabsTrigger>
          )}
          {isAdmin && affiliateAvailable && (
            <TabsTrigger value="affiliate-branding" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Megaphone className="h-4 w-4" />
              Indicação
            </TabsTrigger>
          )}
          {/* Aba Veículos — controla o módulo de veículos do tenant */}
          {isAdmin && (
            <TabsTrigger value="vehicles" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Car className="h-4 w-4" />
              Veículos
            </TabsTrigger>
          )}
          {/* Aba Checklist — liga o módulo de Template de Checklist e Vistorias */}
          {isAdmin && (
            <TabsTrigger value="checklist" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <ListChecks className="h-4 w-4" />
              Checklist
            </TabsTrigger>
          )}
          {/* 2026-10-10: nome visual Gestão Rastreador; chave interna stock preservada. */}
          {isAdmin && (
            <TabsTrigger value="stock" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Boxes className="h-4 w-4" />
              Gestão Rastreador
            </TabsTrigger>
          )}
          {/* 2026-10-08: Aba Ordem de Serviço — liga/desliga o módulo */}
          {isAdmin && (
            <TabsTrigger value="service-orders" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Wrench className="h-4 w-4" />
              Ordem de Serviço
            </TabsTrigger>
          )}
          {/* Aba Tarefas — habilita submódulos Retenção e Jurídico */}
          {isAdmin && (
            <TabsTrigger value="tasks" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <ListChecks className="h-4 w-4" />
              Tarefas
            </TabsTrigger>
          )}
          {/* Aba Workflow — liga/desliga a esteira comercial do tenant */}
          {isAdmin && (
            <TabsTrigger value="workflow" className="shrink-0 gap-2 whitespace-nowrap data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Route className="h-4 w-4" />
              Workflow
            </TabsTrigger>
          )}
          </TabsList>
        </div>

        {/* =====================================================
             Tab Conta — Visual SaaS premium, denso e tipográfico
             ===================================================== */}
        <TabsContent value="account" className="space-y-6">
          {(() => {
            const tenantStatus = currentTenant?.status || 'trial';
            const planKey = (limits?.plan_id as string) || currentTenant?.plan || 'free';
            const planName = (limits?.plan_name as string) || planLabels[planKey] || planKey;
            const initial = (currentTenant?.name || '?').trim().charAt(0).toUpperCase();

            // Linhas de recursos contratados (calculadas a partir da RPC)
            const resourceRows: Array<{
              label: string;
              value: string;
              hint?: string;
            }> = [
              {
                label: 'Agentes',
                value: formatNumber(limits?.effective_max_agents),
                hint: limits?.addon_agents
                  ? `${formatNumber(limits?.effective_max_agents - limits.addon_agents)} base + ${formatNumber(limits.addon_agents)} add-on`
                  : undefined,
              },
              {
                label: 'Inboxes',
                value: formatNumber(limits?.effective_max_inboxes),
                hint: limits?.addon_inboxes
                  ? `${formatNumber(limits?.effective_max_inboxes - limits.addon_inboxes)} base + ${formatNumber(limits.addon_inboxes)} add-on`
                  : undefined,
              },
              {
                label: 'Canais',
                value: formatNumber(limits?.effective_max_channels),
                hint: limits?.addon_channels
                  ? `+${formatNumber(limits.addon_channels)} add-on`
                  : undefined,
              },
              {
                label: 'Instâncias',
                value: formatNumber(limits?.effective_max_instances),
              },
              {
                label: 'Tarefas / mês',
                value: formatNumber(limits?.effective_max_tasks_month),
              },
              {
                label: 'Armazenamento',
                value: formatStorage(limits?.effective_max_tasks_storage_mb),
              },
            ];

            // Módulos ativos (chips)
            const modules: Array<{ label: string; active: boolean }> = [
              { label: 'CRM', active: !!currentTenant?.crm_enabled },
              { label: 'Financeiro', active: !!(limits?.has_financial_module ?? currentTenant?.financial_enabled) },
              { label: 'Tarefas', active: !!currentTenant?.tasks_enabled },
              { label: 'Indicação', active: !!currentTenant?.affiliate_enabled },
              { label: 'Base de Conhecimento', active: (currentTenant as unknown as { knowledge_enabled?: boolean } | null)?.knowledge_enabled !== false },
              { label: 'Campanhas', active: !!limits?.has_campaigns_module },
              { label: 'WhatsApp Oficial', active: !!limits?.has_official_whatsapp },
              { label: 'Contratos Digitais', active: !!limits?.has_digital_contracts_module },
            ];

            return (
              <div className="space-y-6">
                {/* ============= HEADER DO WORKSPACE ============= */}
                <Card className="border-border/60 shadow-none">
                  <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary text-xl font-semibold ring-1 ring-primary/15">
                        {initial}
                      </div>
                      <div className="min-w-0">
                        <h2 className="truncate text-xl font-semibold tracking-tight text-foreground">
                          {currentTenant?.name || '—'}
                        </h2>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span className="font-mono">{currentTenant?.slug || '—'}</span>
                          <span className="text-border">•</span>
                          <button
                            type="button"
                            onClick={() => currentTenant?.id && copy(currentTenant.id, 'tenant-id', 'ID do workspace')}
                            className="group inline-flex items-center gap-1 font-mono uppercase tracking-wider hover:text-foreground transition-colors"
                            title={currentTenant?.id || ''}
                          >
                            ID #{shortId(currentTenant?.id)}
                            {copiedKey === 'tenant-id'
                              ? <Check className="h-3 w-3 text-emerald-500" />
                              : <Copy className="h-3 w-3 opacity-60 group-hover:opacity-100" />}
                          </button>
                          <span className="text-border">•</span>
                          <span>Criado em {formatDate(currentTenant?.created_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                        <span className={`h-1.5 w-1.5 rounded-full ${statusDot[tenantStatus] || 'bg-muted-foreground'}`} />
                        {statusLabels[tenantStatus] || tenantStatus}
                      </Badge>
                      <Badge variant="outline" className="px-2.5 py-1 text-xs font-medium">
                        Plano {planName}
                      </Badge>
                    </div>
                  </div>
                </Card>

                {/* ============= GRID: Workspace + Plano ============= */}
                <div className="grid gap-6 lg:grid-cols-5">
                  {/* Bloco Workspace */}
                  <Card className="border-border/60 shadow-none lg:col-span-2">
                    <div className="border-b border-border/50 px-6 py-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Workspace
                      </p>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        Identificação e estado da conta
                      </p>
                    </div>
                    <dl className="divide-y divide-border/40">
                      <InfoRow label="Nome">
                        <span className="font-medium text-foreground">{currentTenant?.name || '—'}</span>
                      </InfoRow>
                      <InfoRow label="Slug">
                        <button
                          type="button"
                          onClick={() => currentTenant?.slug && copy(currentTenant.slug, 'slug', 'Slug')}
                          className="group inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 font-mono text-xs hover:bg-muted/70 transition-colors"
                        >
                          {currentTenant?.slug || '—'}
                          {copiedKey === 'slug'
                            ? <Check className="h-3 w-3 text-emerald-500" />
                            : <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />}
                        </button>
                      </InfoRow>
                      <InfoRow label="Identificador">
                        <button
                          type="button"
                          onClick={() => currentTenant?.id && copy(currentTenant.id, 'uuid', 'Identificador')}
                          className="group inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                          title={currentTenant?.id || ''}
                        >
                          {shortId(currentTenant?.id)}
                          {copiedKey === 'uuid'
                            ? <Check className="h-3 w-3 text-emerald-500" />
                            : <Copy className="h-3 w-3 opacity-50 group-hover:opacity-100" />}
                        </button>
                      </InfoRow>
                      <InfoRow label="Status">
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                          <span className={`h-1.5 w-1.5 rounded-full ${statusDot[tenantStatus] || 'bg-muted-foreground'}`} />
                          {statusLabels[tenantStatus] || tenantStatus}
                        </span>
                      </InfoRow>
                      <InfoRow label="Criado em">
                        <span className="text-sm text-foreground">{formatDate(currentTenant?.created_at)}</span>
                      </InfoRow>
                      {currentTenant?.trial_ends_at && (
                        <InfoRow label="Trial até">
                          <span className="text-sm text-foreground">{formatDate(currentTenant.trial_ends_at)}</span>
                        </InfoRow>
                      )}
                    </dl>
                  </Card>

                  {/* Bloco Plano e recursos */}
                  <Card className="border-border/60 shadow-none lg:col-span-3">
                    <div className="flex items-start justify-between gap-3 border-b border-border/50 px-6 py-4">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Plano e recursos
                        </p>
                        <div className="mt-1 flex items-baseline gap-2">
                          <h3 className="text-lg font-semibold tracking-tight text-foreground">
                            {planName}
                          </h3>
                          {limits?.is_enterprise && (
                            <span className="text-[10px] font-medium uppercase tracking-wider text-primary">
                              Enterprise
                            </span>
                          )}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1.5 h-8" asChild>
                        <a href="/tenant/plans">
                          Gerenciar
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </div>

                    {/* Recursos contratados (lista densa em 2 colunas) */}
                    <div className="px-6 py-4">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Recursos contratados
                      </p>
                      {loadingLimits ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                          {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5">
                              <Skeleton className="h-3 w-24" />
                              <Skeleton className="h-3 w-12" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                          {resourceRows.map((row) => (
                            <div
                              key={row.label}
                              className="flex items-center justify-between border-b border-border/40 py-2 last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0"
                            >
                              <dt className="text-sm text-muted-foreground">{row.label}</dt>
                              <dd className="text-right">
                                <span className="text-sm font-semibold tabular-nums text-foreground">
                                  {row.value}
                                </span>
                                {row.hint && (
                                  <p className="text-[10px] text-muted-foreground">{row.hint}</p>
                                )}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </div>

                    {/* Módulos ativos */}
                    <div className="border-t border-border/50 px-6 py-4">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Módulos
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {modules.map((m) => (
                          <span
                            key={m.label}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                              m.active
                                ? 'border-border/70 bg-background text-foreground'
                                : 'border-border/40 bg-muted/30 text-muted-foreground/60 line-through'
                            }`}
                          >
                            {m.active && <Check className="h-3 w-3 text-emerald-500" />}
                            {m.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        {/* Tab de Assinatura Digital (apenas para admins) */}
        {isAdmin && (
          <TabsContent value="signatures">
            <SignatureProvidersSettings />
          </TabsContent>
        )}

        {/* Tab Financeiro — configurações como pro-rata (admins; sem gate de add-on) */}
        {isAdmin && (
          <TabsContent value="financial" className="space-y-6">
            <FinancialSettingsCard />
          </TabsContent>
        )}

        {/* Tab Indicação — branding do painel do afiliado */}
        {isAdmin && affiliateAvailable && (
          <TabsContent value="affiliate-branding">
            <AffiliateBrandingTab />
          </TabsContent>
        )}

        {/* Tab Veículos — ativação do módulo e categorias do tipo "Outros" */}
        {isAdmin && (
          <TabsContent value="vehicles">
            <VehiclesSettingsTab />
          </TabsContent>
        )}

        {/* Tab Checklist — ativação do módulo */}
        {isAdmin && (
          <TabsContent value="checklist">
            <ChecklistSettingsTab />
          </TabsContent>
        )}

        {/* Gestão Rastreador — ativação pela chave interna stock. */}
        {isAdmin && (
          <TabsContent value="stock">
            <StockSettingsTab />
          </TabsContent>
        )}

        {/* Tab Ordem de Serviço — ativação do módulo */}
        {isAdmin && (
          <TabsContent value="service-orders">
            <ServiceOrderSettingsTab />
          </TabsContent>
        )}

        {/* Tab Tarefas — submódulos Retenção e Jurídico */}
        {isAdmin && (
          <TabsContent value="tasks">
            <TasksSettingsTab />
          </TabsContent>
        )}

        {/* Aba Workflow (somente administradores) */}
        {isAdmin && (
          <TabsContent value="workflow">
            <WorkflowSettingsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default TenantSettings;
